import React, { useState, useEffect } from "react";
import DataTable from "../components/DataTable.js"; // Assuming DataTable component is reusable
import { fetchDataFromAPI, saveData, deleteData } from "../components/api.js"; // Assuming these API functions exist
import subscriptionStatus from "../components/SubscriptionStatus.js"; // Assuming subscriptionStatus logic is needed

const getRangeInputProps = (col) => {
  if (col.type === "range") {
    return { min: 0, max: 5, step: 0.5 }; // Limit range inputs to 0-5 with step of 0.5
  }
  return {}; // Return empty object for non-range types
};

const Pricing = () => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const isSubscribed = subscriptionStatus(); // This will check the subscription status (if needed)

  useEffect(() => {
    fetchData();
  }, [currentPage]);
  const sanitizePlans = (plans) =>
    plans.map((plan) => ({
      ...plan,
      features:
        plan.features && typeof plan.features === "object"
          ? JSON.stringify(plan.features)
          : plan.features || "",
    }));
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token"); // Retrieve token for authorization
      const filterParamsObj = {
        page: currentPage,
        limit: 10,
      };

      const filterParams = new URLSearchParams(filterParamsObj).toString();

      // Fetch pricing plans data
      const { data: pricingPlans, pagination } = await fetchDataFromAPI(
        `/api/pricing`,
        filterParams,
        token
      );

      setPricingPlans(sanitizePlans(pricingPlans || []));
      setTotalPages(pagination?.totalPages || 1);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching pricing plans:", err.message);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteData(`/api/pricing/${id}`);
      fetchData();
    } catch (err) {
      console.error("Error deleting pricing plan:", err);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchData();
  };

  const handleEdit = async (id, updatedData) => {
    try {
      await saveData(`/api/pricing/${id}`, updatedData); // Edit the pricing plan data
      fetchData();
    } catch (err) {
      console.error("Error editing pricing plan:", err);
    }
  };

  const handleAddRow = async () => {
    try {
      const newRow = {
        name: "New Plan",
        type: "monthly",
      };

      const result = await saveData("/api/pricing", newRow, "POST");
      console.log("Pricing plan added:", result);
      fetchData();
    } catch (err) {
      console.error("Error adding pricing plan:", err);
    }
  };

  const columns = [
    { header: "Name", accessor: "name" },
    { header: "Description", accessor: "description" },
    {
      header: "Type",
      accessor: "type",
      type: "dropdown",
      options: [
        { value: "monthly", label: "Monthly" },
        { value: "yearly", label: "Yearly" },
        { value: "one-time", label: "one-time" },
        { value: "per_order", label: "per_order" },
      ],
    },
    {
      header: "Per Order Fee",
      accessor: "per_order_fee",
      visibleWhen: (formData) => formData?.type === "per_order", // ← ADD THIS
    },
    { header: "Price", accessor: "price" },
    { header: "Yearly Price", accessor: "yearly_price" },
    { header: "Discount Percentage", accessor: "discount_percentage" },
    { header: "Currency", accessor: "currency" },
    {
      header: "Features",
      accessor: "features",
      // Stringify if it's an object so React can render it
      render: (value) => {
        if (!value) return "";
        if (typeof value === "string") return value;
        return JSON.stringify(value);
      },
    },
    { header: "Trial Period", accessor: "trial_period" },
    { header: "Max Users", accessor: "max_users" },
    { header: "Storage Limit", accessor: "storage_limit" },
    {
      header: "Active",
      accessor: "active",
      type: "dropdown",
      options: [
        { value: "active", label: "active" },
        { value: "inactive", label: "inactive" },
      ],
    },
  ];

  return (
    <DataTable
      title="Pricing Plans"
      columns={columns}
      data={pricingPlans}
      setData={(newData) =>
        setPricingPlans(
          Array.isArray(newData) ? sanitizePlans(newData) : newData
        )
      }
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      onAddRow={handleAddRow}
      actions={[
        {
          label: "Edit",
          onClick: (row) => {
            const updatedData = { ...row, name: "Updated Name" };
            handleEdit(row.id, updatedData);
          },
        },
        {
          label: "Delete",
          onClick: (row) => handleDelete(row.id),
        },
      ]}
      apiEndpoint="/api/pricing"
      itemsPerPage={10}
      fetchData={fetchData}
      getRangeInputProps={getRangeInputProps}
    />
  );
};

export default Pricing;
